'use client';

import { useEffect, useState } from 'react';

/**
 * Single source of truth for parsing factory / self-heal deep links into
 * agent integration UI state. The hub mounts this hook once, consumes
 * the parsed envelope to focus a target, then strips handled query
 * params via `history.replaceState` exactly once.
 *
 * Supported params (existing + new):
 *   - mcpConnect=<catalogId>&mcpServerName=<rowName>
 *   - openapiConnect=<name>
 *   - integrationType=<type>&integrationProvider=<provider>
 *   - mcp_connected=<name> | mcp_error=<code> | openapi_connected=<name>
 *   - integrations=1
 *   - integrationFocus=<kind>
 *   - integrationAction=<action>
 *   - integrationQuery=<text>
 */

export type IntegrationFocusKind =
  | 'featured_mcp'
  | 'custom_mcp'
  | 'openapi'
  | 'platform'
  | 'messaging'
  | 'media_gen'
  | 'oauth_return';

export type IntegrationAction =
  | 'connect'
  | 'configure'
  | 'select_tools'
  | 'bind_account'
  | 'reauth'
  | 'review';

export interface IntegrationDeeplink {
  /** Whether the hub should be opened. */
  openHub: boolean;
  mcpConnect: string | null;
  mcpServerName: string | null;
  openapiConnect: string | null;
  integrationType: string | null;
  integrationProvider: string | null;
  integrationFocus: IntegrationFocusKind | null;
  integrationAction: IntegrationAction | null;
  integrationQuery: string;
  toast:
    | { kind: 'success'; message: string }
    | { kind: 'error'; message: string }
    | null;
  /** Names of params that were consumed and should be stripped. */
  consumed: string[];
}

const PARAMS: string[] = [
  'mcpConnect',
  'mcpServerName',
  'openapiConnect',
  'integrationType',
  'integrationProvider',
  'mcp_connected',
  'mcp_error',
  'openapi_connected',
  'integrations',
  'integrationFocus',
  'integrationAction',
  'integrationQuery',
  'hub',
  'hubBrand',
  'hubMode',
  'hubStatus',
];

export function parseIntegrationDeeplink(search: string): IntegrationDeeplink {
  const sp = new URLSearchParams(search);
  const mcpConnected = sp.get('mcp_connected');
  const mcpError = sp.get('mcp_error');
  const openapiConnected = sp.get('openapi_connected');
  let toast: IntegrationDeeplink['toast'] = null;
  if (mcpError) {
    toast = {
      kind: 'error',
      message: `MCP connect failed: ${mcpError.replace(/_/g, ' ')}`,
    };
  } else if (mcpConnected) {
    toast = { kind: 'success', message: `Connected ${mcpConnected}.` };
  } else if (openapiConnected) {
    toast = { kind: 'success', message: `Connected ${openapiConnected}.` };
  }

  const focus = sp.get('integrationFocus');
  const action = sp.get('integrationAction');
  const isValidFocus = (
    s: string | null,
  ): s is IntegrationFocusKind =>
    s === 'featured_mcp' ||
    s === 'custom_mcp' ||
    s === 'openapi' ||
    s === 'platform' ||
    s === 'messaging' ||
    s === 'media_gen' ||
    s === 'oauth_return';
  const isValidAction = (s: string | null): s is IntegrationAction =>
    s === 'connect' ||
    s === 'configure' ||
    s === 'select_tools' ||
    s === 'bind_account' ||
    s === 'reauth' ||
    s === 'review';

  const mcpConnect = sp.get('mcpConnect');
  const openapiConnect = sp.get('openapiConnect');
  const integrationType = sp.get('integrationType');
  const integrationProvider = sp.get('integrationProvider');
  const integrationsFlag = sp.get('integrations');

  const openHub =
    integrationsFlag === '1' ||
    !!mcpConnect ||
    !!openapiConnect ||
    !!(integrationType && integrationProvider) ||
    !!mcpConnected ||
    !!mcpError ||
    !!openapiConnected;

  const consumed: string[] = [];
  for (const k of PARAMS) {
    if (sp.has(k)) consumed.push(k);
  }

  return {
    openHub,
    mcpConnect: mcpConnect ?? null,
    mcpServerName: sp.get('mcpServerName') ?? null,
    openapiConnect: openapiConnect ?? null,
    integrationType: integrationType ?? null,
    integrationProvider: integrationProvider ?? null,
    integrationFocus: isValidFocus(focus) ? focus : null,
    integrationAction: isValidAction(action) ? action : null,
    integrationQuery: sp.get('integrationQuery') ?? '',
    toast,
    consumed,
  };
}

export function stripIntegrationDeeplink(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  let mutated = false;
  for (const k of PARAMS) {
    if (url.searchParams.has(k)) {
      url.searchParams.delete(k);
      mutated = true;
    }
  }
  if (!mutated) return;
  const next =
    url.pathname +
    (url.searchParams.toString() ? `?${url.searchParams.toString()}` : '') +
    url.hash;
  window.history.replaceState({}, '', next);
}

/**
 * React hook: parse the deeplink once on mount, store it in local state,
 * and provide an explicit `consume()` callback the hub calls after
 * applying the parsed envelope.
 */
export function useIntegrationDeeplink(): {
  deeplink: IntegrationDeeplink | null;
  consume: () => void;
} {
  const [deeplink, setDeeplink] = useState<IntegrationDeeplink | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const parsed = parseIntegrationDeeplink(window.location.search);
    if (parsed.openHub || parsed.toast) {
      setDeeplink(parsed);
    }
    // We deliberately do not strip here. The hub may need to read these
    // values across re-renders (e.g. focus a target after data loads),
    // so it owns the moment of consumption.
  }, []);

  const consume = () => {
    stripIntegrationDeeplink();
    // Keep `deeplink` populated so the hub can finish using the parsed
    // values to focus state; only the URL itself is cleaned up.
  };

  return { deeplink, consume };
}
